import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const KIND_LABELS = {
  scheduling_request: 'Scheduling change', new_clean_request: 'Request another clean', property_update: 'Property update',
  quote_decision: 'Quote decision', service_concern: 'Service concern', intake_update: 'Intake update',
  cleaner_report: 'Job report', supply_request: 'Supply request', time_correction: 'Time correction',
  availability_request: 'Availability change', job_exception: 'Visit outcome', completion_review: 'Completion review',
};
export const STATUS_LABELS = {
  new: 'Sent — awaiting review', in_review: 'In review', waiting: 'Waiting on details',
  approved_awaiting_scheduling: 'Approved — awaiting scheduling', approved: 'Approved', declined: 'Declined', resolved: 'Resolved', withdrawn: 'Withdrawn',
};
const newId = () => window.crypto?.randomUUID?.() || `r-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const DRAFT_KEY = 'gg_request_draft';

// Requester side of Needs Review — client portal and Guru workspace
export const RequestsPanel = ({ headers, kinds, appointments = [], dark = false }) => {
  const [mine, setMine] = useState(null);
  const [f, setF] = useState(() => {
    const blank = { kind: kinds[0], title: '', details: '', appointment_id: '' };
    try { const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY)); return d && kinds.includes(d.kind) ? d : blank; } catch { return blank; }
  });
  const [saving, setSaving] = useState(false);
  const reqId = useRef(newId());
  const load = useCallback(() => axios.get(`${API_URL}/api/review-items/mine`, { headers }).then((r) => setMine(r.data)).catch(() => setMine([])), [headers]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(f)); }, [f]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (f.title.trim().length < 3) { toast.error('Add a short summary'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/review-items`, { kind: f.kind, title: f.title.trim(), details: f.details.trim(), appointment_id: f.appointment_id || null, client_request_id: reqId.current }, { headers });
      toast.success('Sent to the office. This is a request — nothing changes until the team responds.');
      reqId.current = newId(); setF({ kind: kinds[0], title: '', details: '', appointment_id: '' }); sessionStorage.removeItem(DRAFT_KEY); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send — your request is kept, please try again.');
    } finally { setSaving(false); }
  };

  const card = dark ? 'border-zinc-800 bg-zinc-900 text-white' : 'border-[#e7e4db] bg-[#fffefa] text-[#20211d]';
  const muted = dark ? 'text-zinc-400' : 'text-[#77776e]';
  return (
    <section className={`rounded-xl border p-5 ${card}`} data-testid="requests-panel">
      <p className={`text-[10px] tracking-[0.16em] font-bold ${muted}`}>REQUESTS TO THE OFFICE</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div><Label className="text-xs">Type</Label>
          <Select value={f.kind} onValueChange={(v) => set('kind', v)}>
            <SelectTrigger className={dark ? 'bg-zinc-950 border-zinc-700' : ''} data-testid="request-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{kinds.map((k) => <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Related visit (optional)</Label>
          <Select value={f.appointment_id || 'none'} onValueChange={(v) => set('appointment_id', v === 'none' ? '' : v)}>
            <SelectTrigger className={dark ? 'bg-zinc-950 border-zinc-700' : ''} data-testid="request-appointment"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">None</SelectItem>{appointments.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="mt-3"><Label className="text-xs">Summary</Label><Input className={dark ? 'bg-zinc-950 border-zinc-700' : ''} value={f.title} onChange={(e) => set('title', e.target.value)} data-testid="request-title" /></div>
      <div className="mt-3"><Label className="text-xs">Details</Label><Textarea className={dark ? 'bg-zinc-950 border-zinc-700' : ''} rows={3} value={f.details} onChange={(e) => set('details', e.target.value)} data-testid="request-details" /></div>
      <div className="flex items-center justify-between gap-3 mt-3">
        <p className={`text-[11px] ${muted}`}>A request isn't a confirmed change. The team will reply here.</p>
        <Button onClick={submit} disabled={saving} className="bg-[#F56F05] hover:bg-[#C2410C] text-white" data-testid="request-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}</Button>
      </div>
      {mine && mine.length > 0 && (
        <ul className={`mt-5 divide-y ${dark ? 'divide-zinc-800' : 'divide-[#e7e4db]'}`} data-testid="my-requests">
          {mine.map((r) => (
            <li key={r.id} className="py-3 text-sm" data-testid={`my-request-${r.id}`}>
              <div className="flex justify-between gap-3"><span className="font-semibold">{r.title}</span><span className={`text-[11px] ${muted}`}>{STATUS_LABELS[r.status]}</span></div>
              <p className={`text-[11px] ${muted}`}>{KIND_LABELS[r.kind]} · {new Date(r.created_at).toLocaleDateString()}</p>
              {r.response && <p className={`mt-1 text-xs rounded-md p-2 ${dark ? 'bg-zinc-800' : 'bg-[#eef0e6]'}`} data-testid={`my-request-response-${r.id}`}>Office: {r.response}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RequestsPanel;
